/**
 * useConversationEngine — the single headless conversation engine.
 *
 * Generalized from useScoperAiChat (proven AI SDK useChat + transport) and
 * folded in the carry-forward behaviors from the legacy useAgentChat:
 * UUID sanitization, localStorage-style persistence via an injected
 * ConversationStore, settings snapshotting, draft anchoring, editor-flush +
 * atomic apply, and RTK cache invalidation. Surfaces (panel, Scoper) are thin
 * presentational consumers of this hook.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChat } from '@ai-sdk/react';
import type { UIMessage } from 'ai';
import type { AiChatSettings } from '@repo/ai-contract';

import { notifyAiDraftResourceApplied } from '../aiDraftApplyEvents';

import { AuthExpiredError, applyDraftRequest } from './applyDraft';
import { getAccessToken } from './backend';
import { sanitizeDraft } from './sanitize';
import { fitDraftSlideContent } from './fitDraftSlides';
import { buildChatTransport } from './transport';
import type { ConversationEngine, EngineProps } from './types';

import type { AiDraft } from '@/shared/types/chat';
import { useAppDispatch } from '@/shared/hooks/redux';
import { coursesApi } from '@/features/my-courses/coursesApi';
import { unitsApi } from '@/features/units/unitsApi';
import { lessonsApi } from '@/features/lessons/lessonsApi';
import { resourcesApi } from '@/features/resources/resourcesApi';
import type { TextFitWarning } from '@/features/slides/utils/textFit';

const PERSIST_THROTTLE_MS = 2000;

function lastAssistantId(messages: UIMessage[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'assistant') return messages[i].id;
  }
  return null;
}

export function useConversationEngine(props: EngineProps): ConversationEngine {
  const { context, settings, store, debug = false, onData, fetchImpl } = props;
  const dispatch = useAppDispatch();

  const [currentStatus, setCurrentStatus] = useState<string | null>(null);
  const [activeDraft, setActiveDraftState] = useState<AiDraft | null>(null);
  const [activeDraftMessageId, setActiveDraftMessageId] = useState<string | null>(null);
  const [activeDraftWarnings, setActiveDraftWarnings] = useState<TextFitWarning[]>([]);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [isAuthExpired, setIsAuthExpired] = useState(false);
  const [dataError, setDataError] = useState<Error | null>(null);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const rateLimitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs so the stable transport / async closures always see latest values.
  const contextRef = useRef(context);
  contextRef.current = context;
  const settingsSnapshotRef = useRef<AiChatSettings>(settings);
  const activeDraftRef = useRef<AiDraft | null>(activeDraft);
  activeDraftRef.current = activeDraft;
  const currentStatusRef = useRef<string | null>(null);
  const activeDraftIdRef = useRef<string | null>(null);

  const storageKey = useMemo(() => (context ? store.keyFor(context) : null), [context, store]);
  const storageKeyRef = useRef<string | null>(storageKey);
  storageKeyRef.current = storageKey;

  const transport = useMemo(
    () =>
      buildChatTransport({
        contextRef,
        settingsRef: settingsSnapshotRef,
        activeDraftRef,
        debug,
        fetchImpl,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable callback; reads mutable data through refs
    []
  );

  const messagesRef = useRef<UIMessage[]>([]);

  const handleData = useCallback(
    (part: { type: string; data: unknown }) => {
      onData?.(part);

      if (part.type === 'data-status') {
        const d = part.data as { stage?: string; label?: string };
        const label = d.label ?? d.stage ?? null;
        if (label !== currentStatusRef.current) {
          currentStatusRef.current = label;
          setCurrentStatus(label);
        }
      } else if (part.type === 'data-draft') {
        const draft = sanitizeDraft(part.data as AiDraft);
        if (draft.draftId !== activeDraftIdRef.current) {
          activeDraftIdRef.current = draft.draftId;
          setApplyError(null);
          setActiveDraftMessageId(lastAssistantId(messagesRef.current));
          // Run the client-side text-fit pass off-handler, then commit the
          // (possibly mutated) draft to state. If the pass throws, fall back to
          // the unfitted draft so the user can still review and apply.
          void (async () => {
            const targetDraftId = draft.draftId;
            try {
              const { draft: fitted, warnings } = await fitDraftSlideContent(draft);
              if (activeDraftIdRef.current !== targetDraftId) return;
              if (warnings.length > 0) {
                console.warn('[ai-chat] text-fit warnings', warnings);
              }
              setActiveDraftState(fitted);
              setActiveDraftWarnings(warnings);
            } catch (err) {
              console.error('[ai-chat] text-fit pass failed', err);
              if (activeDraftIdRef.current !== targetDraftId) return;
              setActiveDraftState(draft);
              setActiveDraftWarnings([]);
            }
          })();
        }
      } else if (part.type === 'data-error') {
        const d = part.data as { code?: string; error?: string };
        setCurrentStatus(null);
        currentStatusRef.current = null;
        setDataError(new Error(d.error || d.code || 'The AI request failed. Please try again.'));
      }
    },
    [onData]
  );

  const handleFinish = useCallback(() => {
    setCurrentStatus(null);
    currentStatusRef.current = null;
  }, []);

  const handleError = useCallback((err: Error) => {
    console.error('[ai-chat] stream error', err);
    setCurrentStatus(null);
    currentStatusRef.current = null;

    // Attempt to parse the backend JSON error body for a user-friendly message.
    // The AI SDK surfaces non-2xx response bodies as err.message.
    type BackendError = { code?: string; error?: string; details?: { retryAfter?: number } };
    let parsed: BackendError | null = null;
    try {
      const raw: unknown = JSON.parse(err.message);
      if (raw !== null && typeof raw === 'object') {
        parsed = raw as BackendError;
      }
    } catch {
      // err.message is plain text — leave parsed null
    }

    if (parsed?.error) {
      setDataError(new Error(parsed.error));
    }

    if (parsed?.code === 'RATE_LIMIT_EXCEEDED') {
      const retryAfterSecs = parsed.details?.retryAfter ?? 60;
      setIsRateLimited(true);
      if (rateLimitTimerRef.current) clearTimeout(rateLimitTimerRef.current);
      rateLimitTimerRef.current = setTimeout(() => {
        setIsRateLimited(false);
        rateLimitTimerRef.current = null;
      }, retryAfterSecs * 1000);
    }
  }, []);

  const chat = useChat({
    transport,
    experimental_throttle: 50,
    onData: handleData,
    onFinish: handleFinish,
    onError: handleError,
  });

  messagesRef.current = chat.messages;

  // ── Persistence ────────────────────────────────────────────────────────────

  // Hydrate when the context (storage key) changes.
  useEffect(() => {
    if (!storageKey) return;
    const persisted = store.load(storageKey);
    chat.stop();
    if (persisted) {
      chat.setMessages(persisted.messages);
      setActiveDraftState(persisted.activeDraft);
      activeDraftIdRef.current = persisted.activeDraft?.draftId ?? null;
      setActiveDraftMessageId(persisted.activeDraftMessageId);
      setActiveDraftWarnings([]);
    } else {
      chat.setMessages([]);
      setActiveDraftState(null);
      activeDraftIdRef.current = null;
      setActiveDraftMessageId(null);
      setActiveDraftWarnings([]);
    }
    setCurrentStatus(null);
    setApplyError(null);
    setApplying(false);
    setDataError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally re-runs only when storageKey changes; setters are stable
  }, [storageKey]);

  // Throttle-persist while streaming; persist immediately otherwise.
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isStreaming = chat.status === 'streaming' || chat.status === 'submitted';

  useEffect(() => {
    const key = storageKeyRef.current;
    if (!key) return;

    const snapshot = () => {
      const k = storageKeyRef.current;
      if (!k) return;
      if (chat.messages.length === 0 && !activeDraft) {
        store.clear(k);
        return;
      }
      store.save(k, {
        version: 0, // store sets the real version
        updatedAt: Date.now(),
        messages: chat.messages,
        activeDraft,
        activeDraftMessageId,
      });
    };

    if (isStreaming) {
      if (!persistTimerRef.current) {
        persistTimerRef.current = setTimeout(() => {
          persistTimerRef.current = null;
          snapshot();
        }, PERSIST_THROTTLE_MS);
      }
      return;
    }

    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
      persistTimerRef.current = null;
    }
    snapshot();
  }, [chat.messages, activeDraft, activeDraftMessageId, isStreaming, store]);

  useEffect(
    () => () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      if (rateLimitTimerRef.current) clearTimeout(rateLimitTimerRef.current);
    },
    []
  );

  // ── Actions ────────────────────────────────────────────────────────────────

  const sendMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isStreaming || isRateLimited) return;
      // Snapshot settings so mid-stream changes don't affect this request.
      settingsSnapshotRef.current = { ...settings };
      void (async () => {
        const token = await getAccessToken();
        if (!token) {
          setIsAuthExpired(true);
          return;
        }
        setIsAuthExpired(false);
        setDataError(null);
        chat.sendMessage({ text: trimmed });
      })();
    },
    [chat, isRateLimited, isStreaming, settings]
  );

  const stop = useCallback(() => {
    chat.stop();
    setCurrentStatus(null);
    currentStatusRef.current = null;
  }, [chat]);

  const retry = useCallback(() => {
    if (isStreaming) return;
    setDataError(null);
    setIsAuthExpired(false);
    chat.clearError();
    void chat.regenerate();
  }, [chat, isStreaming]);

  const acceptDraft = useCallback(async () => {
    const draft = activeDraftRef.current;
    const ctx = contextRef.current;
    if (!draft || !ctx || ctx.assistantType !== 'course' || !ctx.courseId) return;
    if (applying || isStreaming) return;

    setApplying(true);
    setApplyError(null);
    try {
      const { resourceIds, appliedCount } = await applyDraftRequest(ctx, draft);

      notifyAiDraftResourceApplied({
        draftId: draft.draftId,
        resourceIds,
        appliedAt: Date.now(),
      });

      setActiveDraftState(null);
      activeDraftIdRef.current = null;
      setActiveDraftMessageId(null);
      setActiveDraftWarnings([]);

      const confirmation = `Applied ${appliedCount} change${appliedCount === 1 ? '' : 's'}.`;
      chat.setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: 'assistant',
          parts: [{ type: 'text', text: confirmation }],
        } as UIMessage,
      ]);

      dispatch(coursesApi.util.invalidateTags(['Course']));
      dispatch(unitsApi.util.invalidateTags(['Unit']));
      dispatch(lessonsApi.util.invalidateTags(['Lesson']));
      dispatch(resourcesApi.util.invalidateTags(['Resource', 'ResourceList']));
    } catch (err) {
      if (err instanceof AuthExpiredError) {
        setIsAuthExpired(true);
        setApplyError(err.message);
      } else {
        setApplyError(err instanceof Error ? err.message : 'Draft apply failed.');
      }
    } finally {
      setApplying(false);
    }
  }, [applying, chat, dispatch, isStreaming]);

  const rejectDraft = useCallback(() => {
    if (applying) return;
    setActiveDraftState(null);
    activeDraftIdRef.current = null;
    setActiveDraftMessageId(null);
    setActiveDraftWarnings([]);
    setApplyError(null);
  }, [applying]);

  const clearConversation = useCallback(() => {
    chat.stop();
    chat.setMessages([]);
    setActiveDraftState(null);
    activeDraftIdRef.current = null;
    setActiveDraftMessageId(null);
    setActiveDraftWarnings([]);
    setCurrentStatus(null);
    currentStatusRef.current = null;
    setApplyError(null);
    setDataError(null);
    setIsRateLimited(false);
    if (rateLimitTimerRef.current) {
      clearTimeout(rateLimitTimerRef.current);
      rateLimitTimerRef.current = null;
    }
    const key = storageKeyRef.current;
    if (key) store.clear(key);
  }, [chat, store]);

  // Prefer the human-readable dataError (set by handleError after JSON parsing)
  // over the raw chat.error whose message may be the full JSON response body.
  const normalizedChatError = (() => {
    if (!chat.error) return undefined;
    try {
      const raw: unknown = JSON.parse(chat.error.message);
      if (raw !== null && typeof raw === 'object') {
        const { error } = raw as { error?: string };
        if (error) return new Error(error);
      }
    } catch {
      // not JSON
    }
    return chat.error;
  })();

  return {
    messages: chat.messages,
    status: chat.status,
    error: dataError ?? normalizedChatError ?? undefined,
    currentStatus,
    activeDraft,
    activeDraftMessageId,
    activeDraftWarnings,
    applying,
    applyError,
    isAuthExpired,
    isRateLimited,
    sendMessage,
    stop,
    retry,
    acceptDraft,
    rejectDraft,
    clearConversation,
  };
}
